/* eslint-disable react/destructuring-assignment */

import React, { useEffect, useMemo, useState } from 'react';
import { FilterMultiSelect, Pagination, Search, Tabs } from '@pmi/dsm-react';
import { FilterIcon } from '@pmi/dsm-react/dist/components/icons/16';
import { useSelector } from 'react-redux';
import {
  BundleReportItem,
  ProductReport,
} from '../../../../shared/models/ReportModel';
import { getBundleItems, getStatusCounts, getTypeLabel } from '../../services';
import styles from '../productDetailsReport.module.scss';
import RegisteredUsersTable from './RegisteredUsersTable';
import dsmStyles from '../../../../shared/dsm.module.scss';
import { useGetProductUsersQuery } from '../apiSlice';
import { accountSelector } from '../../../../shared/store/shared/accountSlice';
import {
  SortDirection,
  SortedColumn,
} from '../../../../shared/components/ResponsiveTable/models';
import useDebounce from '../../../../shared/hooks/useDebounce';
import { ProductUsers } from '../../../../shared/models/ProductUsersModel';
import { PMIFormatDate } from '../../../../shared/helpers';
import useBreakpoint from '../../../../shared/hooks/useBreakpoint';
import { ProductTypes } from '../../../../shared/constants';
import usePermissions from '../../../../shared/hooks/usePermissions';
import PERMISSIONS from '../../../../shared/permissions';
import OptedOutChip from '../../../../shared/components/OptedOutChip';

interface RegisteredUsersProps {
  product: ProductReport;
  filterValue: string[];
  dealId: string;
  productId: string;
  startDate: string;
  endDate: string;
  usersTableRef: React.MutableRefObject<any>;
  reportType: string;
  onBundleProductTabChange: (sku: string) => void;
  selectedProductTypeTab: string;
}

function getTableLabels(selectedItem: Pick<ProductReport, 'type'>) {
  if (!selectedItem) return null;
  let tableLabel = 'Redemptions';
  let productTypeLabel = '';

  switch (selectedItem.type) {
    case 'Certification':
      productTypeLabel = 'certification';
      tableLabel = `${tableLabel}`;
      break;
    case ProductTypes.membership:
      productTypeLabel = 'membership';
      tableLabel = `${tableLabel}`;
      break;
    case 'Chapter Membership':
      productTypeLabel = 'chapter membership';
      tableLabel = `${tableLabel}`;
      break;
    case 'eLearning':
      productTypeLabel = 'course';
      tableLabel = `${tableLabel}`;
      break;
    case 'Digital Product':
      productTypeLabel = 'product';
      tableLabel = `${tableLabel}`;
      break;
    default:
      break;
  }

  return [tableLabel, productTypeLabel];
}

export default function RegisteredUsers(props: RegisteredUsersProps) {
  if (!props.selectedProductTypeTab) return null;

  const { currentAccountId } = useSelector(accountSelector);
  const bp = useBreakpoint();
  const items: BundleReportItem[] = props.product.bundle
    ? getBundleItems(props.product)
    : [props.product];

  const [selectedItem, setSelectedItem] = useState(
    props.selectedProductTypeTab
      ? items.find((e) => e.sku === props.selectedProductTypeTab)
      : items[0],
  );

  const itemTabs = items.map((e) => ({
    label: getTypeLabel(e.type),
    value: e.sku,
  }));

  const [tableLabel, productTypeLabel] = getTableLabels(selectedItem);
  const { hasAccess } = usePermissions();
  const isInternalAdmin = hasAccess({
    permissions: [PERMISSIONS.ManageElevatedAdmins],
  });
  const [searchText, setSearchText] = useState('');
  const debouncedSearch = useDebounce(searchText, 1000);
  const [filterValue, setFilterValue] = useState<string[]>(
    props.filterValue ?? [],
  );
  const filterOptions = useMemo(
    () =>
      Object.keys(getStatusCounts(selectedItem)).map((key) => ({
        label: key,
        value: key,
      })),
    [selectedItem],
  );
  // State to store the calculated height
  const [heightFromTop, setHeightFromTop] = useState(null);
  const [isUsersTableSorted, setUsersTableSorted] = useState<boolean>(null);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [hasPageChanges, setHasPageChanges] = useState(false);
  const [productUsersListSort, setProductUsersListSort] =
    useState<SortedColumn>({
      name: 'orderDate',
      direction: 'Desc',
    });
  const getFilter = () => (!filterValue?.length ? [] : filterValue);
  const {
    data: rawUsersData,
    isLoading: usersLoading,
    isFetching: usersFetching,
    refetch,
  } = useGetProductUsersQuery(
    {
      accountId: currentAccountId,
      dealId: props.dealId,
      sku: props.productId,
      page: currentPage,
      pageSize,
      status: getFilter(),
      sortDirection: productUsersListSort.direction as SortDirection,
      sortProperty: productUsersListSort.name,
      StartDate: props.startDate,
      EndDate: props.endDate,
      searchInput: debouncedSearch,
      bundleItemSkus: props.product.bundle
        ? selectedItem.bundleItemSkus ?? [selectedItem.sku]
        : [],
    },
    { refetchOnMountOrArgChange: true },
  );

  const userList: ProductUsers[] = useMemo(
    () => rawUsersData?.items?.slice() ?? [],
    [rawUsersData],
  );
  const processing = usersLoading || (usersFetching && !isUsersTableSorted);

  useEffect(() => {
    if (rawUsersData?.totalItemCount) {
      const count = Math.ceil(rawUsersData.totalItemCount / pageSize);
      setTotalPages((prev) => (prev === count ? prev : count));
    }
  }, [rawUsersData?.totalItemCount, pageSize]);

  const onSearchChange = (e: React.FormEvent<HTMLInputElement>) => {
    setSearchText(e.currentTarget.value);
    setCurrentPage(1);
  };

  const onSearchClear = () => {
    setSearchText('');
  };

  const onPageChange = (page: number) => {
    setCurrentPage(page);
    setTimeout(() => refetch(), 100);
    setHasPageChanges(true);
  };

  const onPageChangeDsmCompatible = (e: unknown, pageNumber: number) => {
    onPageChange(pageNumber);
    setHasPageChanges(true);
  };

  const onPageSizeChange = (size: number) => {
    setCurrentPage(1);
    setPageSize(size);
    setTotalPages(1);
  };

  const onProductUsersSort = (columnName: SortedColumn) => {
    setProductUsersListSort({
      name: columnName.name,
      direction: columnName.direction,
    });
    setUsersTableSorted(true);
  };

  // avoid showing loading indicator when sorting is done
  // until API responds within 1 second
  useEffect(() => {
    if (isUsersTableSorted) {
      setTimeout(() => setUsersTableSorted(null), 1000);
    }
  }, [isUsersTableSorted]);

  useEffect(() => {
    if (hasPageChanges) {
      setTimeout(() => {
        window.scroll({
          // add offset when pageSize is default
          top: pageSize === 10 ? heightFromTop + 160 : heightFromTop,
          behavior: 'smooth',
        });
      }, 300);
      setHasPageChanges(false);
    }
  }, [hasPageChanges]);

  // Calculate the height on component mount and when the window is resized

  useEffect(() => {
    // Function to calculate the height from the top of the page to the <div>
    const calculateDivHeight = () => {
      if (props.usersTableRef.current) {
        const rect = props.usersTableRef.current.getBoundingClientRect();
        const { scrollTop } = document.documentElement;
        const divTop = rect.top + scrollTop;
        setHeightFromTop(divTop);
      }
    };

    // Call the function on component mount
    calculateDivHeight();

    // Attach a scroll event listener to recalculate the height if the page is scrolled
    window.addEventListener('scroll', calculateDivHeight);

    // Clean up the event listener on component unmount
    return () => {
      window.removeEventListener('scroll', calculateDivHeight);
    };
  }, []);

  useEffect(() => {
    setFilterValue(props.filterValue);
  }, [props.filterValue]);

  useEffect(() => {
    setCurrentPage(1);
    setTimeout(() => refetch(), 100);
  }, [props.reportType]);

  useEffect(() => {
    setCurrentPage(1);
    refetch();
  }, [filterValue, debouncedSearch]);

  useEffect(() => {
    setSelectedItem(items.find((e) => e.sku === props.selectedProductTypeTab));
  }, [props.selectedProductTypeTab]);

  return (
    <>
      {/* Scroll ref on onChangePage */}
      <div ref={props.usersTableRef} />

      {/* USERS TABLE DESKTOP */}
      {(bp === 'md' || bp === 'lg' || bp === 'xl' || bp === 'xxl') && (
        <div className={`mt-3 ${styles['users-card']}`}>
          <div className={styles['card-container']}>
            <div className="p-3">
              {itemTabs.length > 1 && (
                <div className="mb-4">
                  <Tabs
                    labels={itemTabs}
                    theme="light"
                    value={itemTabs.findIndex(
                      (e) => e.value === selectedItem.sku,
                    )}
                    onChange={(evt, value) => {
                      setSelectedItem(
                        items.find((e) => e.sku === itemTabs[value].value),
                      );
                      props.onBundleProductTabChange(itemTabs[value].value);
                    }}
                  />
                </div>
              )}
              <h5 className="fw-bold fs-5 mb-2">{tableLabel}</h5>
              <div className="d-flex flex-column flex-sm-row my-3 w-100">
                <div>
                  <Search
                    id="userSearch"
                    placeholder="Search by name or email address"
                    value={searchText}
                    onClear={onSearchClear}
                    onChange={onSearchChange}
                    className={`${dsmStyles.secondarySearch} me-0 me-sm-3`}
                    style={{ width: 330 }}
                  />
                </div>
                <div>
                  {/* FILTER AND SEARCH */}
                  <div className="d-flex w-100">
                    <div className={`d-flex ${styles.filterBySelect}`}>
                      <FilterIcon fontSize="sm" className={styles.filterIcon} />
                      <FilterMultiSelect
                        className={null}
                        label="Filter by status"
                        options={filterOptions}
                        selected={filterValue}
                        onApply={setFilterValue}
                        isFilterForTable
                        fontSize="md"
                        variant="primary"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {processing && userList.length === 0 && (
              <p className="px-3 py-3">Loading...</p>
            )}
            {userList.length > 0 && (
              <RegisteredUsersTable
                list={userList}
                page={currentPage}
                totalPages={totalPages}
                onPageChange={onPageChange}
                onPageSizeChange={onPageSizeChange}
                pageSize={pageSize}
                onSort={onProductUsersSort}
                sortedColumn={productUsersListSort}
                type={selectedItem.type}
                isInternalAdmin={isInternalAdmin}
              />
            )}
            {!processing && userList.length === 0 && (
              <p className={`${styles['no-user-msg']}`}>
                {`No redemptions under this ${productTypeLabel}.`}
              </p>
            )}
          </div>
        </div>
      )}

      {/* USERS TABLE MOBILE */}
      {(bp === 'sm' || bp === 'xs') && (
        <>
          <div className="my-4">
            <div>
              {itemTabs.length > 1 && (
                <div className="mb-4">
                  <Tabs
                    labels={itemTabs}
                    theme="light"
                    value={itemTabs.findIndex(
                      (e) => e.value === selectedItem.sku,
                    )}
                    onChange={(evt, value) => {
                      setSelectedItem(
                        items.find((e) => e.sku === itemTabs[value].value),
                      );
                      props.onBundleProductTabChange(itemTabs[value].value);
                    }}
                  />
                </div>
              )}

              <h5 className="fw-bold fs-5 mb-0">{tableLabel}</h5>
              <div className="d-flex flex-column flex-sm-row my-3 w-100">
                <div>
                  <Search
                    id="userSearch"
                    placeholder="Search by name or email address"
                    value={searchText}
                    onClear={onSearchClear}
                    onChange={onSearchChange}
                    className={`${dsmStyles.secondarySearch} me-0 me-sm-3`}
                    style={{ width: 330 }}
                  />
                </div>
                <div>
                  {/* FILTER AND SEARCH */}
                  <div className="d-flex w-100">
                    <div className={`d-flex ${styles.filterBySelect}`}>
                      <FilterIcon fontSize="sm" className={styles.filterIcon} />
                      <FilterMultiSelect
                        className={null}
                        label="Filter by status"
                        options={filterOptions}
                        selected={filterValue}
                        onApply={setFilterValue}
                        fontSize="md"
                        variant="primary"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* USERS RESULT MOBILE */}
          <div>
            {processing && <p className="py-0">Loading...</p>}
            {userList.length > 0 && (
              <div>
                {userList?.map((user) => (
                  <div
                    className={`${styles['card-container']} p-4 mb-4`}
                    key={user.email}
                  >
                    <span
                      className="fw-bold"
                      data-testid="data-name"
                      style={{ fontSize: 14 }}
                      data-exclude-lg
                    >
                      {user.firstName} {user.lastName}
                    </span>
                    <br />
                    <span
                      className={`${styles['grey-3']} label`}
                      data-testid="data-email"
                      data-exclude-lg
                    >
                      {user.email}
                    </span>
                    {isInternalAdmin && (
                      <>
                        <br />
                        <span
                          className={`${styles['grey-3']} label`}
                          data-testid="data-id"
                        >
                          {`Person ID: ${
                            user.personId === '0' ? '0000000' : user.personId
                          } `}
                        </span>
                      </>
                    )}
                    <OptedOutChip
                      isInternalAdmin={isInternalAdmin}
                      shareData={user.shareData}
                    />
                    <br />
                    <span
                      className={`${styles['grey-3']} label`}
                      data-testid="data-order-id"
                    >
                      {`Order ID: ${user.orderIncrementId}`}
                    </span>
                    <div className="mt-1 d-flex w-100">
                      <span
                        className={`badge ${styles['badge-grey']} fw-normal label`}
                        data-testid="data-status"
                      >
                        {user.secondaryStatus}
                      </span>
                      {(() => {
                        const percentage = Math.round(
                          (!user.progress ? 0 : user.progress / 100) * 100,
                        );

                        return (
                          <div className="ms-2 d-flex justify-content-between align-items-center w-100">
                            <div className={`${styles.outer} w-100`}>
                              <div
                                style={{ width: `${percentage}%` }}
                                className={styles.inner}
                              />
                            </div>
                            <div
                              className="ms-2 label text-end"
                              data-testid="data-percent"
                            >
                              {percentage}%
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                    <div
                      className={`${styles['grey-3']} fw-normal label mt-1`}
                      data-testid="data-status"
                    >
                      Order Date: {PMIFormatDate(user.orderDate)}
                    </div>
                    {user.eventDateUTC && (
                      <div
                        className={`${styles['grey-3']} fw-normal label mt-1`}
                        data-testid="data-status"
                      >
                        Exam Date: {PMIFormatDate(user.eventDateUTC)}
                      </div>
                    )}
                  </div>
                ))}
                <Pagination
                  data-testid="test-pagination"
                  className=""
                  disabled={false}
                  totalPages={totalPages}
                  showPageSizes
                  pageSizes={[
                    {
                      label: '10 per page',
                      value: '10',
                    },
                    {
                      label: '25 per page',
                      value: '25',
                    },
                    {
                      label: '50 per page',
                      value: '50',
                    },
                    {
                      label: '100 per page',
                      value: '100',
                    },
                  ]}
                  onChange={onPageChangeDsmCompatible}
                  onPageSizeChange={onPageSizeChange}
                  currentPage={currentPage}
                  pageSize={pageSize}
                />
              </div>
            )}
            {!processing && userList.length === 0 && (
              <div className={`${styles['card-container']} p-4 mb-4`}>
                <p className={`${styles['no-user-msg']}`}>
                  {`No enrolled users under this ${productTypeLabel}.`}
                </p>
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
}
